/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { type ActiveFeatures, canAccessBetaGroup, getGroup, Group, standards } from '@g17eco/core';
import { getScopeInfoByMap } from '@features/survey-scope/scopeSelection';
import { ScopeGroups } from '../../../../types/surveyCommon';
import { ToolbarButtonProps } from './ToolbarButton';
import config from '../../../../config';
import { QUESTION } from '@constants/terminology';
import UniversalTrackerClassInterface from '../../../../model/UniversalTrackerClassInterface';
import { SurveyModelMinData } from '../../../../types/survey';

interface StandardButtonProps {
  utr: UniversalTrackerClassInterface;
  selectedStandard: string;
  handleStandardsClick: (value: string) => void;
}

export interface StandardsButtons extends StandardButtonProps {
  standardShow: number;
  showSelectedStandard?: boolean;
  survey: Pick<SurveyModelMinData, 'scope'>;
  features: ActiveFeatures | undefined;
}

interface StandardButton extends StandardButtonProps {
  standardCode: string;
}

const getTooltip = (utr: UniversalTrackerClassInterface, code: any) => {
  return (
    <div className='m-1 text-left'>
      <div className='strong'>{getGroup('standards', code)?.shortName} {utr.getTypeCode(code)}</div>
      <div>{utr.getValueLabel(code)}</div>
      <div>Click to see full {QUESTION.CAPITALIZED_SINGULAR} Text</div>
    </div>
  )
}

export const getStandardButton = ({ utr, selectedStandard, standardCode, handleStandardsClick }: StandardButton) => {
  const standard = getGroup('standards', standardCode);
  if (!standard) {
    return { contents: <></> }
  }
  return {
    contents: <div className='img-container'> <img className='icon-img' src={standard.src} alt={standard.name} /></div>,
    tooltip: getTooltip(utr, standardCode),
    btnClass: selectedStandard === standardCode ? 'active' : '',
    onClick: () => handleStandardsClick(standardCode)
  }
}

const append = (acc: Group[], toAdd: Group) => [...acc, toAdd];
const replace = (acc: Group[], toRemove: Group, toAdd: Group) => [...acc.filter(g => g.code !== toRemove.code), toAdd];

const standardsReducer = (acc: Group[], newStandard: Group, scopeCodes: string[]): Group[] => {

  if (!newStandard.versionGroupCode || !newStandard.version) {
    return append(acc, newStandard);
  }

  const existingStandard = acc.find(s => s.versionGroupCode === newStandard.versionGroupCode);
  if (existingStandard) {

    const isNewInScope = scopeCodes.includes(newStandard.code);
    const isExistingInScope = scopeCodes.includes(existingStandard.code);
    if (isNewInScope && isExistingInScope) {
      // If both are in scope, show both icons
      return append(acc, newStandard);
    }

    // If only one in scope, prioritize that one
    if (!isNewInScope && isExistingInScope) {
      return acc;
    }
    if (isNewInScope && !isExistingInScope) {
      return replace(acc, existingStandard, newStandard);
    }

    // Neither are in scope, so choose latest version only
    const existingVersion = existingStandard?.version ?? 0;
    if (newStandard.version > existingVersion) {
      return replace(acc, existingStandard, newStandard);
    }
    return acc;
  }
  return append(acc, newStandard);
}

export const getStandardButtons = (props: StandardsButtons) => {
  const { utr, survey, features } = props;

  const scopeCodes = survey?.scope?.standards ?? [];
  const isInScope = (standard: Group) => {
    const { inScope, isPartial } = getScopeInfoByMap(survey?.scope, standard.code, ScopeGroups.Standards, []);
    return inScope || isPartial;
  };

  const items: ToolbarButtonProps[] = Object.values(standards)
    .filter(standard => utr.hasAlternativeInfo(standard.code))
    // A metric reused by a beta standard keeps that alternative, so only show it to companies with the beta
    .filter(standard => canAccessBetaGroup(standard, features) || isInScope(standard))
    .reduce((acc, standard) => standardsReducer(acc, standard, scopeCodes), [] as Group[])
    .map(standard => getStandardButton({ ...props, standardCode: standard.code }));

  if (items.length === 0) {
    const utrType = utr.getType();
    if (utrType === 'custom_kpi') {
      items.push({
        contents: <i className='fa fa-crosshairs fa-2x' title='Custom KPI' />,
        tooltip: utr.getValueLabel(undefined),
        btnClass: 'active',
      });
    } else {
      items.push({
        contents: <img src={`${config.media.logosBaseUrl}/wwg.png`} alt='WWG' />,
        tooltip: utr.getValueLabel(undefined),
        btnClass: 'active',
        onClick: () => props.handleStandardsClick('wwg')
      });
    }
  }
  return items;
}
